// umlaut-check: skip — "Zustaendigkeit*" sind technische Bezeichner
// (Datei-/Komponentennamen), bleiben ASCII wie die Datei selbst.
import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {Alert, Button, Container} from "react-bootstrap";
import {usePage} from "@inertiajs/react";
import PurposeBox from "../../Components/SalesAnalysis/PurposeBox.jsx";
import ZustaendigkeitStatusTiles from "../../Components/SalesAnalysis/ZustaendigkeitStatusTiles.jsx";
import ZustaendigkeitFilterBar from "../../Components/SalesAnalysis/ZustaendigkeitFilterBar.jsx";
import StickyFilterBlock from "../../Components/StickyFilterBlock.jsx";
import ZustaendigkeitTable from "../../Components/SalesAnalysis/ZustaendigkeitTable.jsx";
import useDebouncedValue from "../../Components/SalesAnalysis/useDebouncedValue.js";
import useAnalysisData from "../../Components/SalesAnalysis/useAnalysisData.js";
import {useQueryListState, useQueryState} from "../../Components/SalesAnalysis/useQueryState.js";
import {STANDARD_STATUS_FILTER} from "../../Components/SalesAnalysis/zustaendigkeitStatus.js";
import {friendlyErrorMessage} from "../../Components/SalesAnalysis/format.js";
import {gemerkteWerteLesen, gemerkteWerteSchreiben} from "../../Components/Filter/useGemerkteFilter.jsx";

const PER_PAGE = 100;

/**
 * Differenzansicht Zuständigkeit (AP8, lesender Teil, E16): vergleicht die
 * maßgebliche Vorlage (Fassung Oscar) mit dem tatsächlichen Zuständigen in
 * weclapp. Reine Anzeige - das Ändern der Zuständigkeit mit sofortigem
 * Rückschreiben nach weclapp folgt in einem späteren Schritt.
 */
export default function SalesAnalysisZustaendigkeit() {
    const [meta, setMeta] = useState(null);
    const [repsError, setRepsError] = useState(null);

    // T12: Local-Storage-Rückfall wie in useAnalysisFilters.js - ohne
    // eigenen URL-Parameter greift der zuletzt gemerkte Stand je Benutzer,
    // ein mitgegebener URL-Parameter hat weiterhin Vorrang.
    const benutzerId = usePage().props.auth?.id;
    const speicherSchluessel = `filter.vertrieb-zustaendigkeit${benutzerId !== undefined && benutzerId !== null ? "." + benutzerId : ""}`;
    const gemerkt = useMemo(() => gemerkteWerteLesen(speicherSchluessel), [speicherSchluessel]);

    const [person, setPerson] = useQueryState("person", gemerkt.person ?? "");
    // Standardfilter beim ersten Öffnen: nur die auffälligen Status (Bauauftrag
    // Punkt 3) - ohne Status-Parameter in der URL greift zunächst der
    // gemerkte Stand, sonst STANDARD_STATUS_FILTER; ein Link mit eigenem
    // status= überschreibt beides wie gehabt.
    const [status, setStatus] = useQueryListState("status", gemerkt.status || STANDARD_STATUS_FILTER);
    const [searchInput, setSearchInput] = useState(gemerkt.search ?? "");
    const search = useDebouncedValue(searchInput, 400);
    const [page, setPage] = useState(1);

    useEffect(() => {
        gemerkteWerteSchreiben(speicherSchluessel, {person, status, search: searchInput});
    }, [speicherSchluessel, person, status, searchInput]);

    useEffect(() => {
        let active = true;
        axios
            .get("/vertrieb/daten/filter")
            .then(({data}) => {
                if (active) {
                    setMeta({...data, ...(data?.meta || {})});
                }
            })
            .catch((e) => {
                if (active) {
                    setRepsError(friendlyErrorMessage(e, "Die Vertreterliste konnte nicht geladen werden."));
                }
            });
        return () => {
            active = false;
        };
    }, []);

    // Seite auf 1 zurücksetzen, sobald sich ein Filter ändert - sonst könnte
    // eine leere Folgeseite angezeigt werden.
    useEffect(() => {
        setPage(1);
    }, [person, status, search]);

    const params = {
        person: person || undefined,
        status,
        search: search || undefined,
        page,
        per_page: PER_PAGE,
    };

    const {data, loading, error} = useAnalysisData("/vertrieb/daten/zustaendigkeit", params);

    const toggleStatus = (value) => {
        setStatus(status.includes(value) ? status.filter((s) => s !== value) : [...status, value]);
    };

    const rows = data?.rows || [];
    const lastPage = data?.last_page || 1;

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox />
            <h1 className="h4 mb-2">Zuständigkeit</h1>
            <Alert variant="secondary" className="py-2 px-3 small mb-3">
                Ändern der Zuständigkeit folgt; maßgeblich ist der Kundenverantwortliche in weclapp (E16).
            </Alert>

            {repsError && <div className="alert alert-warning">{repsError}</div>}

            <ZustaendigkeitStatusTiles counts={data?.counts} selected={status} onToggle={toggleStatus} />

            <StickyFilterBlock
                summary={[
                    person ? (person === "pool" ? "Nicht zugeordnet" : person) : "Alle Personen",
                    `${status.length} Status`,
                    searchInput ? `„${searchInput}“` : null,
                ].filter(Boolean).join(" · ")}
            >
                <ZustaendigkeitFilterBar
                    reps={meta?.reps}
                    person={person}
                    onPersonChange={setPerson}
                    status={status}
                    onStatusChange={setStatus}
                    search={searchInput}
                    onSearchChange={setSearchInput}
                />
            </StickyFilterBlock>

            <ZustaendigkeitTable rows={rows} loading={loading} error={error} meta={meta} />

            {!loading && !error && data?.total > 0 && (
                <div className="d-flex align-items-center justify-content-between mt-2 small text-muted">
                    <span>{data.total} Kunden · Seite {data.page} von {lastPage}</span>
                    <div className="d-flex gap-2">
                        <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                            Zurück
                        </Button>
                        <Button size="sm" variant="outline-secondary" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>
                            Weiter
                        </Button>
                    </div>
                </div>
            )}
        </Container>
    );
}
